import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../components/AuthContext';
import TileSelectionModal from '../../components/TileSelectionModal';
import { 
  FaPlus, 
  FaMinus, 
  FaSearch, 
  FaTimes, 
  FaEye, 
  FaFilter, 
  FaDownload,
  FaBox,
  FaPuzzlePiece,
  FaWarehouse,
  FaTruck,
  FaCalendarAlt,
  FaFileAlt,
  FaSave,
  FaHistory,
  FaChevronLeft,
  FaChevronRight,
  FaMapMarkerAlt,
  FaStickyNote,
  FaEdit,
  FaTrash,
  FaFileExport,
  FaUndo,
  FaBoxes,
  FaLayerGroup,
  FaUser,
  FaCog,
  FaTh,
  FaList,
  FaHashtag,
  FaArrowDown,
  FaChevronUp,
  FaChevronDown,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import Select from 'react-select';
import MasterSelection from '../../components/MasterSelection';

const TilesInward = () => {
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tiles, setTiles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [recipientType, setRecipientType] = useState('Customer');
  const [sourceType, setSourceType] = useState('supplier'); // 'supplier' or 'recipient'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState("");
  const [items, setItems] = useState([{
    tileId: null,
    tileName: '',
    tileDetails: null,
    freshStockBox: 0,
    freshStockPieces: 0,
    cornerDamagedBox: 0,
    cornerDamagedPieces: 0,
    fullDamagedBox: 0,
    fullDamagedPieces: 0,
    availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
  }]);

  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(null);
  
  // Inward history state with enhanced pagination
  const [inwardHistory, setInwardHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [totalHistory, setTotalHistory] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedInward, setSelectedInward] = useState(null);
  const [historyFilters, setHistoryFilters] = useState({
    location: null,
    startDate: null,
    endDate: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [activeTab, setActiveTab] = useState('entry');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [viewMode, setViewMode] = useState('list');

  // Refs for focus management
  const freshStockBoxRefs = useRef([]);
  const freshStockPiecesRefs = useRef([]);
  const cornerDamagedBoxRefs = useRef([]);
  const cornerDamagedPiecesRefs = useRef([]);
  const fullDamagedBoxRefs = useRef([]);
  const fullDamagedPiecesRefs = useRef([]);
  const filterDateStartRef = useRef(null);
  const filterDateEndRef = useRef(null);

  // Initial data loading
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load inward history when filters or pagination changes
  useEffect(() => {
    fetchInwardHistory();
  }, [historyPage, historyLimit, historyFilters]);

 

  // Update available stock when location changes
  useEffect(() => {
    if (selectedLocation) {
      const updateAvailableStock = async () => {
        const updatedItems = await Promise.all(
          items.map(async (item) => {
            if (item.tileId) {
              const stock = await fetchTileStock(item.tileId, selectedLocation.value);
              return { ...item, availableStock: stock };
            }
            return item;
          })
        );
        setItems(updatedItems);
      };
      updateAvailableStock();
    }
  }, [selectedLocation]);

  // Update refs when items change
  useEffect(() => {
    freshStockBoxRefs.current = freshStockBoxRefs.current.slice(0, items.length);
    freshStockPiecesRefs.current = freshStockPiecesRefs.current.slice(0, items.length);
    cornerDamagedBoxRefs.current = cornerDamagedBoxRefs.current.slice(0, items.length);
    cornerDamagedPiecesRefs.current = cornerDamagedPiecesRefs.current.slice(0, items.length);
    fullDamagedBoxRefs.current = fullDamagedBoxRefs.current.slice(0, items.length);
    fullDamagedPiecesRefs.current = fullDamagedPiecesRefs.current.slice(0, items.length);
  }, [items.length]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const companyId = user?.user?.company?._id || user?.user?.company;
      const [tilesRes, locationsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles?userId=${user.user._id}&includeNoStock=true`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`${process.env.REACT_APP_API_BASE_URL}/locations/companyId/${companyId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (tilesRes.ok) {
        const tilesData = await tilesRes.json();
        setTiles(Array.isArray(tilesData) ? tilesData : tilesData.data || []);
      }

      if (locationsRes.ok) {
        const locationsData = await locationsRes.json();
        setLocations(Array.isArray(locationsData) ? locationsData : locationsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load initial data',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchInwardHistory = async () => {
    setHistoryLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: historyPage.toString(),
        limit: historyLimit.toString(),
        userId: user.user._id,
        ...(historyFilters.location && { location: historyFilters.location }),
        ...(historyFilters.startDate && { startDate: historyFilters.startDate }),
        ...(historyFilters.endDate && { endDate: historyFilters.endDate })
      });

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/inward-history?${queryParams}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();

      if (data.success) {
        let historyData = data.data.inwardHistory || data.data || data.history || [];
        const total = data.data?.total || data.total || data.totalCount || 0;
        
        if (!Array.isArray(historyData)) {
          historyData = [];
        }
        
        setInwardHistory(historyData);
        setTotalHistory(total);
        setTotalPages(Math.ceil(total / historyLimit));
      } else {
        setInwardHistory([]);
        setTotalHistory(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('Error fetching inward history:', error);
      setInwardHistory([]);
      setTotalHistory(0);
      setTotalPages(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryFilterChange = (field, value) => {
    setHistoryFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setHistoryPage(1);
  };

  const resetHistoryFilters = () => {
    setHistoryFilters({
      location: null,
      startDate: null,
      endDate: null
    });
    setHistoryPage(1);
  };

  const fetchTileStock = async (tileId, locationId) => {
    try {
      const tile = tiles.find(t => t._id === tileId);
      const defaults = { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 };
      if (!tile || !tile.stock) return defaults;

      const locationStock = tile.stock.find(s => s.location._id === locationId || s.location === locationId);
      if (locationStock) {
        return {
          freshStockBox: locationStock.freshStockBox || 0,
          freshStockPieces: locationStock.freshStockPieces || 0,
          cornerDamagedBox: locationStock.cornerDamagedBox || 0,
          cornerDamagedPieces: locationStock.cornerDamagedPieces || 0,
          fullDamagedBox: locationStock.fullDamagedBox || 0,
          fullDamagedPieces: locationStock.fullDamagedPieces || 0
        };
      }
      
      return defaults;
    } catch (error) {
      console.error('Error fetching tile stock:', error);
      return { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 };
    }
  };

  const handleTileSelect = async (tile, index) => {
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Select Location',
        text: 'Please select a location first',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    const stock = await fetchTileStock(tile._id, selectedLocation.value);
    
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      tileId: tile._id,
      tileName: tile.tilesName,
      tileDetails: tile,
      availableStock: stock
    };
    setItems(updatedItems);
    setShowTilesModal(false);
    setCurrentItemIndex(null);

    setTimeout(() => {
      freshStockBoxRefs.current[index]?.focus();
    }, 100);
  };

  const addItem = () => {
    setItems([...items, {
      tileId: null,
      tileName: '',
      tileDetails: null,
      freshStockBox: 0,
      freshStockPieces: 0,
      cornerDamagedBox: 0,
      cornerDamagedPieces: 0,
      fullDamagedBox: 0,
      fullDamagedPieces: 0,
      availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
    }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const handleKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const fieldOrder = ['freshStockBox', 'freshStockPieces', 'cornerDamagedBox', 'cornerDamagedPieces', 'fullDamagedBox', 'fullDamagedPieces'];
      const currentFieldIndex = fieldOrder.indexOf(field);
      
      if (currentFieldIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentFieldIndex + 1];
        const refMap = {
          freshStockBox: freshStockBoxRefs,
          freshStockPieces: freshStockPiecesRefs,
          cornerDamagedBox: cornerDamagedBoxRefs,
          cornerDamagedPieces: cornerDamagedPiecesRefs,
          fullDamagedBox: fullDamagedBoxRefs,
          fullDamagedPieces: fullDamagedPiecesRefs
        };
        refMap[nextField]?.current[index]?.focus();
      } else if (index < items.length - 1) {
        freshStockBoxRefs.current[index + 1]?.focus();
      } else {
        addItem();
        setTimeout(() => {
          freshStockBoxRefs.current[items.length]?.focus();
        }, 100);
      }
    } else if (e.key === 'F2') {
      e.preventDefault();
      setCurrentItemIndex(index);
      setShowTilesModal(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a location',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    if (sourceType === 'supplier' && !selectedSupplier) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please select a supplier',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    if (sourceType === 'recipient' && !selectedRecipient) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: `Please select a ${recipientType.toLowerCase()}`,
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    const validItems = items.filter(item => 
      item.tileId && 
      (item.freshStockBox > 0 || item.freshStockPieces > 0 || item.cornerDamagedBox > 0 || item.cornerDamagedPieces > 0 || item.fullDamagedBox > 0 || item.fullDamagedPieces > 0)
    );

    if (validItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Valid Items',
        text: 'Please add at least one item with quantity',
        customClass: { popup: 'swal-dark-mode' }
      });
      return;
    }

    setSubmitting(true);
    try {
      const inwardData = {
        location: selectedLocation.value,
        ...(sourceType === 'supplier' ? { supplier: selectedSupplier.value } : {}),
        ...(sourceType === 'recipient' ? { recipient: selectedRecipient.value, recipientType } : {}),
        date: selectedDate,
        remarks,
        items: validItems.map(item => ({
          tileId: item.tileId,
          freshStockBox: item.freshStockBox,
          freshStockPieces: item.freshStockPieces,
          cornerDamagedBox: item.cornerDamagedBox,
          cornerDamagedPieces: item.cornerDamagedPieces,
          fullDamagedBox: item.fullDamagedBox,
          fullDamagedPieces: item.fullDamagedPieces
        })),
        createdBy: user.user._id || user.user.id
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/tiles/inward`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(inwardData)
      });

      const data = await response.json();

      if (data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: 'Tiles inward entry created successfully',
          customClass: { popup: 'swal-dark-mode' }
        });
        
        // Reset form
        setItems([{
          tileId: null,
          tileName: '',
          tileDetails: null,
          freshStockBox: 0,
          freshStockPieces: 0,
          cornerDamagedBox: 0,
          cornerDamagedPieces: 0,
          fullDamagedBox: 0,
          fullDamagedPieces: 0,
          availableStock: { freshStockBox: 0, freshStockPieces: 0, cornerDamagedBox: 0, cornerDamagedPieces: 0, fullDamagedBox: 0, fullDamagedPieces: 0 }
        }]);
        setRemarks('');
        setSelectedSupplier(null);
        setSelectedRecipient(null);
        
        // Refresh history
        fetchInwardHistory();
        
        // Switch to history tab
        setActiveTab('history');
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: data.message || 'Failed to create inward entry',
          customClass: { popup: 'swal-dark-mode' }
        });
      }
    } catch (error) {
      console.error('Error creating inward entry:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create inward entry',
        customClass: { popup: 'swal-dark-mode' }
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewDetails = (inward) => {
    setSelectedInward(inward);
    setShowDetailsModal(true);
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Date', 'Location', 'Supplier', 'Tile Name', 'Fresh Stock (Box)', 'Fresh Stock (Pieces)', 'Corner Damaged (Box)', 'Corner Damaged (Pcs)', 'Full Damaged (Box)', 'Full Damaged (Pcs)', 'Remarks'],
      ...inwardHistory.flatMap(entry => 
        entry.items.map(item => [
          formatDate(entry.date),
          entry.location?.name || '',
          entry.supplier?.name || '',
          item.tileId?.tilesName || 'N/A',
          item.freshStockBox,
          item.freshStockPieces,
          item.cornerDamagedBox || 0,
          item.cornerDamagedPieces || 0,
          item.fullDamagedBox || 0,
          item.fullDamagedPieces || 0,
          entry.remarks || ''
        ])
      )
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tiles-inward-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-100' : 'text-gray-800'}`}>Tiles Inward</h1>
          <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage tiles inward entries and track stock movement</p>
        </div>

        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('entry')}
              className={`${
                activeTab === 'entry'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode 
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaBox className="inline mr-2" />
              New Inward Entry
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`${
                activeTab === 'history'
                  ? 'border-blue-500 text-blue-600'
                  : darkMode 
                    ? 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
            >
              <FaHistory className="inline mr-2" />
              Inward History
            </button>
          </nav>
        </div>

        {/* Entry Tab */}
        {activeTab === 'entry' && (
          <div className={`rounded-xl shadow-sm border transition-colors duration-300 ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <div className={`p-6 border-b transition-colors duration-300 ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <h2 className={`text-xl font-semibold transition-colors duration-300 ${
                darkMode ? 'text-gray-100' : 'text-gray-800'
              }`}>Create New Inward Entry</h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <FaMapMarkerAlt className="inline mr-1" />
                    Location *
                  </label>
                  <Select
                    value={selectedLocation}
                    onChange={setSelectedLocation}
                    options={locations.map(location => ({
                      value: location._id,
                      label: location.name
                    }))}
                    placeholder="Select location..."
                    className="react-select-container"
                    classNamePrefix="react-select"
                    isSearchable
                    required
                    styles={{
                      control: (base) => ({
                        ...base,
                        backgroundColor: darkMode ? '#374151' : '#ffffff',
                        borderColor: darkMode ? '#4B5563' : '#D1D5DB',
                        '&:hover': { borderColor: darkMode ? '#6B7280' : '#9CA3AF' }
                      }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor: darkMode ? '#374151' : '#ffffff'
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isFocused
                          ? (darkMode ? '#4B5563' : '#EFF6FF')
                          : (darkMode ? '#374151' : '#ffffff'),
                        color: darkMode ? '#ffffff' : '#111827'
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: darkMode ? '#ffffff' : '#111827'
                      }),
                      input: (base) => ({
                        ...base,
                        color: darkMode ? '#ffffff' : '#111827'
                      })
                    }}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Source Type *
                  </label>
                  <div className="flex rounded-lg overflow-hidden border transition-colors duration-300 " style={{ borderColor: darkMode ? '#4B5563' : '#D1D5DB' }}>
                    <button
                      type="button"
                      onClick={() => { setSourceType('supplier'); setSelectedRecipient(null); }}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1 ${
                        sourceType === 'supplier'
                          ? 'bg-blue-600 text-white shadow-inner'
                          : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <FaTruck className="w-3 h-3" /> Supplier
                    </button>
                    <button
                      type="button"
                      onClick={() => { setSourceType('recipient'); setSelectedSupplier(null); }}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-all duration-200 flex items-center justify-center gap-1 ${
                        sourceType === 'recipient'
                          ? 'bg-purple-600 text-white shadow-inner'
                          : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <FaUser className="w-3 h-3" /> Customer/Dealer
                    </button>
                  </div>
                </div>

                <div>
                  {sourceType === 'supplier' ? (
                    <>
                      <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <FaTruck className="inline mr-1" />
                        Supplier *
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowSupplierModal(true)}
                        className={`w-full text-left px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100 hover:border-gray-500' 
                            : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                        }`}
                      >
                        {selectedSupplier ? selectedSupplier.label : 'Select supplier...'}
                      </button>
                    </>
                  ) : (
                    <>
                      <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <FaUser className="inline mr-1" />
                        {recipientType} *
                      </label>
                      <div className="flex gap-2 mb-2">
                        {['Customer', 'Dealer'].map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => { setRecipientType(type); setSelectedRecipient(null); }}
                            className={`px-3 py-1 text-xs font-medium rounded-full transition-all duration-200 ${
                              recipientType === type
                                ? type === 'Customer' ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-400' : 'bg-purple-100 text-purple-700 ring-1 ring-purple-400'
                                : darkMode ? 'bg-gray-600 text-gray-300 hover:bg-gray-500' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowRecipientModal(true)}
                        className={`w-full text-left px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100 hover:border-gray-500' 
                            : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
                        }`}
                      >
                        {selectedRecipient ? selectedRecipient.label : `Select ${recipientType.toLowerCase()}...`}
                      </button>
                    </>
                  )}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <FaCalendarAlt className="inline mr-1" />
                    Inward Date *
                  </label>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <FaStickyNote className="inline mr-1" />
                    Remarks
                  </label>
                  <input
                    type="text"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                      darkMode 
                        ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }`}
                  />
                </div>
              </div>

              {/* Items Section */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-100' : 'text-gray-800'
                  }`}>Items</h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                  >
                    <FaPlus className="mr-2" />
                    Add Item
                  </button>
                </div>

                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className={`p-4 rounded-lg border transition-colors duration-300 ${
                      darkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex justify-between items-start mb-4">
                        <h4 className={`font-medium transition-colors duration-300 ${
                          darkMode ? 'text-gray-100' : 'text-gray-800'
                        }`}>Item {index + 1}</h4>
                        {items.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
                        {/* Tile Selection */}
                        <div className="lg:col-span-1">
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>Tile *</label>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowTilesModal(true);
                            }}
                            className={`w-full px-3 py-2 border rounded-lg hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          >
                            {item.tileName || 'Select tile...'}
                          </button>
                        </div>

                        {/* Stock inputs */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Fresh Stock (Box)
                          </label>
                          <input
                            ref={el => freshStockBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.freshStockBox}
                            onChange={(e) => handleItemChange(index, 'freshStockBox', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Fresh Stock (Pieces)
                          </label>
                          <input
                            ref={el => freshStockPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.freshStockPieces}
                            onChange={(e) => handleItemChange(index, 'freshStockPieces', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'freshStockPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Corner Dmg (Box)
                          </label>
                          <input
                            ref={el => cornerDamagedBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.cornerDamagedBox}
                            onChange={(e) => handleItemChange(index, 'cornerDamagedBox', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Corner Dmg (Pcs)
                          </label>
                          <input
                            ref={el => cornerDamagedPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.cornerDamagedPieces}
                            onChange={(e) => handleItemChange(index, 'cornerDamagedPieces', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'cornerDamagedPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Full Dmg (Box)
                          </label>
                          <input
                            ref={el => fullDamagedBoxRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.fullDamagedBox}
                            onChange={(e) => handleItemChange(index, 'fullDamagedBox', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'fullDamagedBox')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                            darkMode ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Full Dmg (Pcs)
                          </label>
                          <input
                            ref={el => fullDamagedPiecesRefs.current[index] = el}
                            type="number"
                            min="0"
                            value={item.fullDamagedPieces}
                            onChange={(e) => handleItemChange(index, 'fullDamagedPieces', parseInt(e.target.value) || 0)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'fullDamagedPieces')}
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                              darkMode 
                                ? 'bg-gray-700 border-gray-600 text-gray-100' 
                                : 'bg-white border-gray-300 text-gray-900'
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitting || loading}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors duration-200 flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </>
                  ) : (
                    <>
                      <FaSave className="mr-2" />
                      Create Inward Entry
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            {/* Header Section */}
            <div className={`rounded-xl border p-6 transition-colors duration-300 ${
              darkMode 
                ? 'bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-800' 
                : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex items-center space-x-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    darkMode ? 'bg-green-900/50' : 'bg-green-100'
                  }`}>
                    <FaArrowDown className="text-green-600 text-xl" />
                  </div>
                  <div>
                    <h2 className={`text-2xl font-bold transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-800'
                    }`}>Inward History</h2>
                    <p className={`transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-600'
                    }`}>Track and manage tiles inward entries</p>
                    <div className="flex items-center mt-2 space-x-4 text-sm">
                      <span className={`px-3 py-1 rounded-full font-medium ${
                        darkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-800'
                      }`}>
                        {totalHistory} Total Entries
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-3">
                  <div className={`flex items-center rounded-lg border p-1 ${
                    darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                  }`}>
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-all duration-200 ${
                        viewMode === 'grid' 
                          ? 'bg-green-500 text-white shadow-sm' 
                          : darkMode 
                            ? 'text-gray-400 hover:text-green-400 hover:bg-green-900/30'
                            : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title="Grid View"
                    >
                      <FaTh className="text-sm" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-all duration-200 ${
                        viewMode === 'list' 
                          ? 'bg-green-500 text-white shadow-sm' 
                          : darkMode 
                            ? 'text-gray-400 hover:text-green-400 hover:bg-green-900/30'
                            : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
                      }`}
                      title="List View"
                    >
                      <FaList />
                    </button>
                  </div>
                  
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center"
                  >
                    <FaFilter className="mr-2" />
                    {showFilters ? 'Hide Filters' : 'Filters'}
                  </button>
                  
                  <button
                    onClick={exportToCSV}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-200 flex items-center"
                  >
                    <FaFileExport className="mr-2" />
                    Export
                  </button>
                </div>
              </div>

              {showFilters && (
                <div className={`mt-6 p-4 rounded-lg border transition-colors duration-300 ${
                  darkMode 
                    ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-800' 
                    : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
                }`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <FaMapMarkerAlt className="inline mr-1" /> Location
                      </label>
                      <Select
                        value={locations.find(loc => loc._id === historyFilters.location) ? {
                          value: historyFilters.location,
                          label: locations.find(loc => loc._id === historyFilters.location)?.name
                        } : null}
                        onChange={(option) => handleHistoryFilterChange('location', option?.value || null)}
                        options={locations.map(location => ({
                          value: location._id,
                          label: location.name
                        }))}
                        placeholder="All locations"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (base) => ({
                            ...base,
                            backgroundColor: darkMode ? '#374151' : '#ffffff',
                            borderColor: darkMode ? '#4B5563' : '#D1D5DB',
                            '&:hover': { borderColor: darkMode ? '#6B7280' : '#9CA3AF' }
                          }),
                          menu: (base) => ({
                            ...base,
                            backgroundColor: darkMode ? '#374151' : '#ffffff'
                          }),
                          option: (base, state) => ({
                            ...base,
                            backgroundColor: state.isFocused
                              ? (darkMode ? '#4B5563' : '#EFF6FF')
                              : (darkMode ? '#374151' : '#ffffff'),
                            color: darkMode ? '#ffffff' : '#111827'
                          }),
                          singleValue: (base) => ({
                            ...base,
                            color: darkMode ? '#ffffff' : '#111827'
                          }),
                          input: (base) => ({
                            ...base,
                            color: darkMode ? '#ffffff' : '#111827'
                          })
                        }}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <FaCalendarAlt className="inline mr-1" /> Start Date
                      </label>
                      <input
                        ref={filterDateStartRef}
                        type="date"
                        value={historyFilters.startDate || ''}
                        onChange={(e) => handleHistoryFilterChange('startDate', e.target.value || null)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>

                    <div>
                      <label className={`block text-sm font-medium mb-2 transition-colors duration-300 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        <FaCalendarAlt className="inline mr-1" /> End Date
                      </label>
                      <input
                        ref={filterDateEndRef}
                        type="date"
                        value={historyFilters.endDate || ''}
                        onChange={(e) => handleHistoryFilterChange('endDate', e.target.value || null)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                          darkMode 
                            ? 'bg-gray-700 border-gray-600 text-gray-100' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>

                    <div className="flex items-end">
                      <button
                        onClick={resetHistoryFilters}
                        className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 flex justify-center items-center"
                      >
                        <FaUndo className="mr-2" />
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6">
              {historyLoading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading inward history...</p>
                  </div>
                </div>
              ) : inwardHistory.length === 0 ? (
                <div className="text-center py-12">
                  <FaBox className={`mx-auto h-16 w-16 mb-4 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
                  <h3 className={`text-lg font-medium mb-2 transition-colors duration-300 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>No inward entries found</h3>
                  <p className={`transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Try adjusting your filters or create a new inward entry.</p>
                </div>
              ) : (
                <>
                  {viewMode === 'grid' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {inwardHistory.map((entry) => (
                        <div key={entry._id} className={`border rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${
                          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                              <div className="flex items-center space-x-3">
                                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                  darkMode ? 'bg-green-900/50' : 'bg-green-100'
                                }`}>
                                  <FaArrowDown className="text-green-600" />
                                </div>
                                <div>
                                  <h3 className={`font-semibold transition-colors duration-300 ${
                                    darkMode ? 'text-gray-100' : 'text-gray-900'
                                  }`}>{entry.inwardId || 'N/A'}</h3>
                                  <p className={`text-sm transition-colors duration-300 ${
                                    darkMode ? 'text-gray-400' : 'text-gray-500'
                                  }`}>{formatDate(entry.date)}</p>
                                </div>
                              </div>
                              <button
                                onClick={() => setExpandedItems(prev => 
                                  prev.has(entry._id) 
                                    ? new Set([...prev].filter(id => id !== entry._id))
                                    : new Set([...prev, entry._id])
                                )}
                                className={`transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                                }`}
                              >
                                {expandedItems.has(entry._id) ? <FaChevronUp /> : <FaChevronDown />}
                              </button>
                            </div>

                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Location:</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {entry.location?.name || 'N/A'}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Supplier:</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${
                                  darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-800'
                                }`}>
                                  {entry.supplier?.name || 'N/A'}
                                </span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Items:</span>
                                <span className={`font-medium transition-colors duration-300 ${
                                  darkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}>{entry.items?.length || 0}</span>
                              </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Total Boxes:</span>
                                <span className="font-bold text-green-600">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0), 0) || 0}
                                </span>
                              </div>

                              <div className="flex items-center justify-between"> 
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Total Pieces:</span>
                                <span className="font-bold text-green-600">{entry.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0) + (item.fullDamagedPieces || 0), 0) || 0}</span>
                                </div>

                              <div className="flex items-center justify-between">
                                <span className={`text-sm transition-colors duration-300 ${
                                  darkMode ? 'text-gray-400' : 'text-gray-600'
                                }`}>Created By:</span>
                                <span className={`text-sm font-medium transition-colors duration-300 ${
                                  darkMode ? 'text-gray-100' : 'text-gray-900'
                                }`}>
                                  {entry.createdBy?.name}
                                </span>
                              </div>

                              {expandedItems.has(entry._id) && (
                                <div className={`mt-4 pt-4 border-t transition-colors duration-300 ${
                                  darkMode ? 'border-gray-700' : 'border-gray-200'
                                }`}>
                                  <div className="space-y-2">
                                    <div className="text-sm">
                                      <span className={`font-medium ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Remarks:</span>
                                      <p className={`mt-1 p-2 rounded ${darkMode ? 'text-gray-300 bg-gray-700/50' : 'text-gray-700 bg-gray-50'}`}>
                                        {entry.remarks || 'No remarks provided'}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                              <button
                                onClick={() => handleViewDetails(entry)}
                                className={`w-full px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center ${
                                  darkMode ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                }`}
                              >
                                <FaEye className="mr-2" />
                                View Details
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {viewMode === 'list' && (
                    <div className="overflow-x-auto">
                      <table className={`min-w-full divide-y border rounded-lg ${darkMode ? 'divide-gray-700 border-gray-700' : 'divide-gray-200 border-gray-200'}`}>
                        <thead className={darkMode ? 'bg-gray-800' : 'bg-gray-50'}>
                          <tr>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaHashtag className="inline mr-1" /> Inward ID
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaCalendarAlt className="inline mr-1" /> Date
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaMapMarkerAlt className="inline mr-1" /> Location
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaTruck className="inline mr-1" /> Supplier
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaBoxes className="inline mr-1" /> Items
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaLayerGroup className="inline mr-1" /> Total Boxes
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaBoxes className="inline mr-1" /> Total Pieces
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaUser className="inline mr-1" /> Created By
                            </th>
                            <th className={`px-4 py-3 text-left text-xs font-medium uppercase ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              <FaCog className="inline mr-1" /> Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${darkMode ? 'bg-gray-800/50 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                          {inwardHistory.map((entry) => (
                            <tr key={entry._id} className={`transition-colors duration-150 ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{entry.inwardId}</td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                {formatDate(entry.date)}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-blue-900/50 text-blue-400' : 'bg-blue-100 text-blue-800'}`}>
                                  {entry.location?.name || 'Unknown'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-purple-900/50 text-purple-400' : 'bg-purple-100 text-purple-800'}`}>
                                  {entry.supplier?.name || 'Unknown'}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                {entry.items?.length || 0}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                <span className="text-green-600 font-bold">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0), 0)}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium">
                                <span className="text-green-600 font-bold">
                                  {entry.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0) + (item.fullDamagedPieces || 0), 0)}
                                </span>
                              </td>
                              <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                                {entry.createdBy?.name || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <button
                                  onClick={() => handleViewDetails(entry)}
                                  className={`transition-colors duration-200 flex items-center ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                                >
                                  <FaEye className="mr-1" /> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {totalHistory > historyLimit && (
                    <div className="mt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                        Showing <span className="font-medium">{((historyPage - 1) * historyLimit) + 1}</span> to{' '}
                        <span className="font-medium">{Math.min(historyPage * historyLimit, totalHistory)}</span> of{' '}
                        <span className="font-medium">{totalHistory}</span> entries
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setHistoryPage(1)}
                          disabled={historyPage === 1}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-600 hover:bg-gray-700' : 'border-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          First
                        </button>
                        <button
                          onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}
                          disabled={historyPage === 1}
                          className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-600 hover:bg-gray-700' : 'border-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          <FaChevronLeft />
                        </button>
                        
                        <div className="flex items-center space-x-1">
                          {[...Array(Math.min(5, totalPages))].map((_, i) => {
                            const pageNumber = historyPage <= 3 ? i + 1 : 
                                             historyPage >= totalPages - 2 ? totalPages - 4 + i :
                                             historyPage - 2 + i;
                            
                            if (pageNumber < 1 || pageNumber > totalPages) return null;
                            
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => setHistoryPage(pageNumber)}
                                className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${
                                  historyPage === pageNumber
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : darkMode
                                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
                                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => setHistoryPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={historyPage >= totalPages}
                          className={`px-3 py-2 border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-600 hover:bg-gray-700' : 'border-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          <FaChevronRight />
                        </button>
                        <button
                          onClick={() => setHistoryPage(totalPages)}
                          disabled={historyPage >= totalPages}
                          className={`px-3 py-2 text-sm border rounded-lg transition-colors duration-200 ${darkMode ? 'border-gray-600 text-gray-300 disabled:bg-gray-800 disabled:text-gray-600 hover:bg-gray-700' : 'border-gray-300 text-gray-700 disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-50'}`}
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Tiles Selection Modal */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => { setShowTilesModal(false); setCurrentItemIndex(null); }}
        onSelect={(tile) => handleTileSelect(tile, currentItemIndex)}
        darkMode={darkMode}
        userId={user?.user?._id || user?.user?.id}
        locationId={selectedLocation?.value}
        filterByLocation={false}
        showNetStock={true}
      />

      {/* Details Modal */}
      {showDetailsModal && selectedInward && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden transition-colors duration-300 ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className={`px-6 py-4 border-b transition-colors duration-300 ${
              darkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center justify-between">
                <h3 className={`text-xl font-semibold transition-colors duration-300 ${
                  darkMode ? 'text-gray-100' : 'text-gray-900'
                }`}>Inward Details - {selectedInward.inwardId || 'N/A'}</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className={`transition-colors ${
                    darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <FaTimes className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className={`font-medium mb-3 transition-colors duration-300 ${
                    darkMode ? 'text-gray-100' : 'text-gray-900'
                  }`}>Inward Information</h4>
                  <div className={`space-y-2 text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    <div><span className="font-medium">Inward ID:</span> {selectedInward.inwardId || 'N/A'}</div>
                    <div><span className="font-medium">Date:</span> {formatDate(selectedInward.date)}</div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Location:</span> 
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        {selectedInward.location?.name || 'N/A'}
                      </span>
                    </div>
                    <div><span className="font-medium">Supplier:</span> {selectedInward.supplier?.name || 'N/A'}</div>
                    <div><span className="font-medium">Created By:</span> {selectedInward.createdBy?.name}</div>
                    <div><span className="font-medium">Total Items:</span> {selectedInward.items?.length || 0}</div>
                    <div><span className="font-medium">Total Boxes:</span> {selectedInward.items?.reduce((total, item) => total + (item.freshStockBox || 0) + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0), 0) || 0}</div>
                    <div><span className="font-medium">Total Pieces:</span> {selectedInward.items?.reduce((total, item) => total + (item.freshStockPieces || 0) + (item.cornerDamagedPieces || 0) + (item.fullDamagedPieces || 0), 0) || 0}</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Remarks</h4>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm min-h-[100px]">
                    {selectedInward.remarks || 'No remarks provided'}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Inward Items</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tile</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fresh Stock</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Corner Damaged</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Damaged</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedInward.items?.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-sm">{item.tileId?.tilesName || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{item.tileId?.id || 'N/A'}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-green-600">
                              {item.freshStockBox || 0} Boxes, {item.freshStockPieces || 0} Pieces
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-yellow-600">
                              {item.cornerDamagedBox || 0} Box, {item.cornerDamagedPieces || 0} Pcs
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <div className="text-red-600">
                              {item.fullDamagedBox || 0} Box, {item.fullDamagedPieces || 0} Pcs
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {(item.freshStockBox + (item.cornerDamagedBox || 0) + (item.fullDamagedBox || 0)) || 0} Boxes
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Supplier Selection Modal */}
      <MasterSelection
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSelect={(supplier) => {
          setSelectedSupplier({ value: supplier._id, label: supplier.name });
          setShowSupplierModal(false);
        }}
        allowedTypes={['Supplier']}
        defaultType="Supplier"
        title="Select Supplier"
      />

      {/* Recipient (Customer/Dealer) Selection Modal */}
      <MasterSelection
        isOpen={showRecipientModal}
        onClose={() => setShowRecipientModal(false)}
        onSelect={(entity, type) => {
          setSelectedRecipient({
            value: entity._id,
            label: type === 'Customer' ? `${entity.name} (${entity.phone || ''})` : entity.businessName || entity.name
          });
          if (type) setRecipientType(type);
          setShowRecipientModal(false);
        }}
        allowedTypes={['Customer', 'Dealer']}
        defaultType={recipientType}
        title={`Select ${recipientType}`}
      />
    </div>
  );
};

export default TilesInward;